// =========================================================================
// McCain / farming and food security — Q3 2026 (McCain_July26)
//
// Schema:  mccain   (sibling of tracking / single / energy)
// Tables:  constituency, ward, local_authority
// Wave:    1
//
// Column names are the raw McCain_July26_* names from the stratification
// script's model_pred_names. This schema does NOT use the
// <domain>:<question>:<answer>:cat convention, so no keys are shared with
// the tracking or energy schemas.
//
// The extra categorical cut on this schema is `voting_intention`
// (codes 1-11 from VI_map), sitting where `segment` sits in tracking.
//
// Question and answer wording is verbatim from the July 2026 questionnaire.
// Question numbers are given in the section comments below.
//
// THREE THINGS TO BE AWARE OF WHEN READING THESE NUMBERS
//
//  1. BFG.2 (effective_farmers_fair) was a FILTERED question — only asked
//     of respondents who said farmers are mostly or almost always paid
//     unfairly at BFG.1. The stratified values are spread across the whole
//     population, which is not the same as the survey base. Treat with care
//     and label it in the UI.
//     BFG.1 (farmers_paid_fair) IS now modelled, but its columns do not form
//     a distribution and so cannot supply the filter base — see the note on
//     that group below before using either question.
//
//  2. THREE QUESTIONS ARE MULTI-SELECT, CAPPED: SF.7 (up to three),
//     BFG.2 (up to two). The stratification normalises each group to sum
//     to 1, so these values are shares of all selections made — NOT the
//     percentage of people who picked each option. A single-select chart
//     will read as if they were.
//
//  3. Two items in the questionnaire have no model and so no column here:
//     the fourth FS.5 item (reducing greenhouse gas emissions on farms),
//     and the six non-farming sectors in the SF.1 grid. FS.5's grid below
//     therefore shows three of four items.
// =========================================================================

export default {
  // --- SF.1 ---------------------------------------------------------------
  // Farming item only. The grid also covered manufacturing, construction,
  // utilities, transportation, tech and telecomms, and financial services —
  // none of which were modelled.
  // Net positive is Essential + Very important, with Fairly important as the
  // neutral point. Almost everyone says farming matters, so netting the top
  // two discriminates better than netting the top three.
  farming_importance: {
    type: "likert",
    label: "Importance of farming to daily life",
    category: "mccain",
    fulltext: "How important do you think each of the following sectors are to daily life in the UK? Please base your answer on anything you consider relevant. - Farming",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_farming_importance_essential",
      "McCain_July26_farming_importance_very",
      "McCain_July26_farming_importance_fairly",
      "McCain_July26_farming_importance_not_very",
      "McCain_July26_farming_importance_not_at_all",
      "McCain_July26_farming_importance_dk",
    ],
    labels: {
      "McCain_July26_farming_importance_essential": "Essential",
      "McCain_July26_farming_importance_very": "Very important",
      "McCain_July26_farming_importance_fairly": "Fairly important",
      "McCain_July26_farming_importance_not_very": "Not very important",
      "McCain_July26_farming_importance_not_at_all": "Not at all important",
      "McCain_July26_farming_importance_dk": "Don't know",
    },
    colors: {
      "McCain_July26_farming_importance_essential": "#1B5E20",
      "McCain_July26_farming_importance_very": "#2E7D32",
      "McCain_July26_farming_importance_fairly": "#66BB6A",
      "McCain_July26_farming_importance_not_very": "#EF5350",
      "McCain_July26_farming_importance_not_at_all": "#C62828",
      "McCain_July26_farming_importance_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_farming_importance_essential", "McCain_July26_farming_importance_very"],
      neu: "McCain_July26_farming_importance_fairly",
      neg: ["McCain_July26_farming_importance_not_very", "McCain_July26_farming_importance_not_at_all"],
      dk: "McCain_July26_farming_importance_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // --- SF.7 ---------------------------------------------------------------
  // MULTI-SELECT, up to three picks. Values are shares of all selections,
  // not the percentage of respondents choosing each option.
  farming_problems: {
    type: "categorical",
    label: "Biggest problems facing farmers",
    category: "mccain",
    fulltext: "What do you feel are the biggest problems facing farmers in the UK today?",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_problems_rising_costs",
      "McCain_July26_problems_enviro_obligations",
      "McCain_July26_problems_pressure_prices",
      "McCain_July26_problems_next_gen",
      "McCain_July26_problems_accessing_labour",
      "McCain_July26_problems_farming_families",
      "McCain_July26_problems_volatile_weather",
      "McCain_July26_problems_foreign_competition",
      "McCain_July26_problems_mental_health",
      "McCain_July26_problems_government_support",
      "McCain_July26_problems_else",
      "McCain_July26_problems_nota",
      "McCain_July26_problems_dk",
    ],
    labels: {
      "McCain_July26_problems_rising_costs": "Rising costs (e.g. of fertiliser, feed and energy)",
      "McCain_July26_problems_enviro_obligations": "Meeting environmental obligations (e.g. on carbon emissions, biodiversity)",
      "McCain_July26_problems_pressure_prices": "Pressure to keep prices low",
      "McCain_July26_problems_next_gen": "Attracting the next generation to farming",
      "McCain_July26_problems_accessing_labour": "Accessing labour needed to work the land",
      "McCain_July26_problems_farming_families": "Being able to pass on land within farming families",
      "McCain_July26_problems_volatile_weather": "Volatile weather patterns",
      "McCain_July26_problems_foreign_competition": "Competition from foreign farmers",
      "McCain_July26_problems_mental_health": "Mental health and stress",
      "McCain_July26_problems_government_support": "Receiving adequate financial support from the government",
      "McCain_July26_problems_else": "Something else",
      "McCain_July26_problems_nota": "None of the above",
      "McCain_July26_problems_dk": "Don't know",
    },
    colors: {
      "McCain_July26_problems_rising_costs": "#1565C0",
      "McCain_July26_problems_enviro_obligations": "#2E7D32",
      "McCain_July26_problems_pressure_prices": "#F57C00",
      "McCain_July26_problems_next_gen": "#7B1FA2",
      "McCain_July26_problems_accessing_labour": "#0097A7",
      "McCain_July26_problems_farming_families": "#C62828",
      "McCain_July26_problems_volatile_weather": "#558B2F",
      "McCain_July26_problems_foreign_competition": "#AD1457",
      "McCain_July26_problems_mental_health": "#4E342E",
      "McCain_July26_problems_government_support": "#00897B",
      "McCain_July26_problems_else": "#F9A825",
      "McCain_July26_problems_nota": "#9E9E9E",
      "McCain_July26_problems_dk": "#B0B8C4",
    },
    schema: "mccain",
    waves: [1],
  },

  // --- FS.2 ---------------------------------------------------------------
  // Asked after being told the true figure is ~65%, so this is an informed
  // preference, not a top-of-mind one.
  local_food_production: {
    type: "likert",
    label: "Desired share of food produced in the UK",
    category: "mccain",
    fulltext: "Currently, around 65% of food consumed in the UK is produced here. Thinking about this, do you think we should aim to produce a higher, lower or similar proportion of the food we consume here in the UK?",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_local_food_higher",
      "McCain_July26_local_food_same",
      "McCain_July26_local_food_lower",
      "McCain_July26_local_food_dk",
    ],
    labels: {
      "McCain_July26_local_food_higher": "We should aim to produce a higher proportion",
      "McCain_July26_local_food_same": "We should aim to produce the same proportion",
      "McCain_July26_local_food_lower": "We should aim to produce a lower proportion",
      "McCain_July26_local_food_dk": "Don't know",
    },
    colors: {
      "McCain_July26_local_food_higher": "#1739e5",
      "McCain_July26_local_food_same": "#6B7280",
      "McCain_July26_local_food_lower": "#FF6D00",
      "McCain_July26_local_food_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_local_food_higher"],
      neu: "McCain_July26_local_food_same",
      neg: ["McCain_July26_local_food_lower"],
      dk: "McCain_July26_local_food_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // --- FS.5 ---------------------------------------------------------------
  // Three of the questionnaire's four items. The fourth — reducing
  // greenhouse gas emissions on farms — was not modelled, so it has no
  // column and cannot be shown here.
  farming_regen_importance: {
    type: "likert_grid",
    label: "Protecting the future of farming",
    netLabel: "importance",
    fulltext: "How important or not do you think each of the following are for protecting the future of farming in the UK?",
    sidebarCompareSubtitle: "Compare net scores across all items, or drill into one.",
    sidebarCompareButton: "Compare all aspects",
    sidebarDrillTitle: "Drill into an aspect",
    sidebarDrillSubtitle: "Select an item for individual results.",
    sidebarDrillSubtitleTarget: "Select one item for targeting.",
    client: ["mccain", "stack-data-strategy"],
    category: "mccain",
    subVariables: [
      "regen_soil",
      "regen_habitats",
      "regen_water",
    ],
    subLabels: {
      "regen_soil": "Looking after soil",
      "regen_habitats": "Protecting natural habitats",
      "regen_water": "Ensuring access to water",
    },
    subColors: {
      "regen_soil": "#6D4C41",
      "regen_habitats": "#2E7D32",
      "regen_water": "#0288D1",
    },
    // Flat union of all sub-variable keys
    keys: [
      "McCain_July26_regen_soil_very",
      "McCain_July26_regen_soil_fairly",
      "McCain_July26_regen_soil_not_very",
      "McCain_July26_regen_soil_not_at_all",
      "McCain_July26_regen_soil_dk",
      "McCain_July26_regen_habitats_very",
      "McCain_July26_regen_habitats_fairly",
      "McCain_July26_regen_habitats_not_very",
      "McCain_July26_regen_habitats_not_at_all",
      "McCain_July26_regen_habitats_dk",
      "McCain_July26_regen_water_very",
      "McCain_July26_regen_water_fairly",
      "McCain_July26_regen_water_not_very",
      "McCain_July26_regen_water_not_at_all",
      "McCain_July26_regen_water_dk",
    ],
    labels: {},
    schema: "mccain",
    waves: [1],
  },

  regen_soil: {
    type: "likert",
    hidden: true,
    label: "Protecting farming's future: looking after soil",
    category: "mccain",
    fulltext: "How important or not do you think each of the following are for protecting the future of farming in the UK? Looking after soil - for example preventing erosion, using organic fertiliser to make it more productive, reducing the toxicity of pesticides to soil",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_regen_soil_very",
      "McCain_July26_regen_soil_fairly",
      "McCain_July26_regen_soil_not_very",
      "McCain_July26_regen_soil_not_at_all",
      "McCain_July26_regen_soil_dk",
    ],
    labels: {
      "McCain_July26_regen_soil_very": "Very important",
      "McCain_July26_regen_soil_fairly": "Fairly important",
      "McCain_July26_regen_soil_not_very": "Not very important",
      "McCain_July26_regen_soil_not_at_all": "Not at all important",
      "McCain_July26_regen_soil_dk": "Don't know",
    },
    colors: {
      "McCain_July26_regen_soil_very": "#2E7D32",
      "McCain_July26_regen_soil_fairly": "#66BB6A",
      "McCain_July26_regen_soil_not_very": "#EF5350",
      "McCain_July26_regen_soil_not_at_all": "#C62828",
      "McCain_July26_regen_soil_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_regen_soil_very", "McCain_July26_regen_soil_fairly"],
      neg: ["McCain_July26_regen_soil_not_very", "McCain_July26_regen_soil_not_at_all"],
      dk: "McCain_July26_regen_soil_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  regen_habitats: {
    type: "likert",
    hidden: true,
    label: "Protecting farming's future: protecting natural habitats",
    category: "mccain",
    fulltext: "How important or not do you think each of the following are for protecting the future of farming in the UK? Protecting natural habitats in and around farms",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_regen_habitats_very",
      "McCain_July26_regen_habitats_fairly",
      "McCain_July26_regen_habitats_not_very",
      "McCain_July26_regen_habitats_not_at_all",
      "McCain_July26_regen_habitats_dk",
    ],
    labels: {
      "McCain_July26_regen_habitats_very": "Very important",
      "McCain_July26_regen_habitats_fairly": "Fairly important",
      "McCain_July26_regen_habitats_not_very": "Not very important",
      "McCain_July26_regen_habitats_not_at_all": "Not at all important",
      "McCain_July26_regen_habitats_dk": "Don't know",
    },
    colors: {
      "McCain_July26_regen_habitats_very": "#2E7D32",
      "McCain_July26_regen_habitats_fairly": "#66BB6A",
      "McCain_July26_regen_habitats_not_very": "#EF5350",
      "McCain_July26_regen_habitats_not_at_all": "#C62828",
      "McCain_July26_regen_habitats_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_regen_habitats_very", "McCain_July26_regen_habitats_fairly"],
      neg: ["McCain_July26_regen_habitats_not_very", "McCain_July26_regen_habitats_not_at_all"],
      dk: "McCain_July26_regen_habitats_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  regen_water: {
    type: "likert",
    hidden: true,
    label: "Protecting farming's future: ensuring access to water",
    category: "mccain",
    fulltext: "How important or not do you think each of the following are for protecting the future of farming in the UK? Ensuring access to the water needed by farms for irrigation",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_regen_water_very",
      "McCain_July26_regen_water_fairly",
      "McCain_July26_regen_water_not_very",
      "McCain_July26_regen_water_not_at_all",
      "McCain_July26_regen_water_dk",
    ],
    labels: {
      "McCain_July26_regen_water_very": "Very important",
      "McCain_July26_regen_water_fairly": "Fairly important",
      "McCain_July26_regen_water_not_very": "Not very important",
      "McCain_July26_regen_water_not_at_all": "Not at all important",
      "McCain_July26_regen_water_dk": "Don't know",
    },
    colors: {
      "McCain_July26_regen_water_very": "#2E7D32",
      "McCain_July26_regen_water_fairly": "#66BB6A",
      "McCain_July26_regen_water_not_very": "#EF5350",
      "McCain_July26_regen_water_not_at_all": "#C62828",
      "McCain_July26_regen_water_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_regen_water_very", "McCain_July26_regen_water_fairly"],
      neg: ["McCain_July26_regen_water_not_very", "McCain_July26_regen_water_not_at_all"],
      dk: "McCain_July26_regen_water_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // --- FS.6 ---------------------------------------------------------------
  // Categorical rather than likert: 'I don't think the weather has changed'
  // sits outside the harder/easier scale, so a net score would be misleading.
  // If you want a net, compute it in the UI over the five scale points only.
  climate_change_farming: {
    type: "categorical",
    label: "Impact of changed weather on farming",
    category: "mccain",
    fulltext: "Over the last decade, the weather in the UK has changed in different ways: for example with higher maximum temperatures and increased rainfall in winter. Regardless of what you think has caused this, do you think this has...",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_weather_considerably_harder",
      "McCain_July26_weather_somewhat_harder",
      "McCain_July26_weather_no_impact",
      "McCain_July26_weather_somewhat_easier",
      "McCain_July26_weather_considerably_easier",
      "McCain_July26_weather_not_changed",
      "McCain_July26_weather_dk",
    ],
    labels: {
      "McCain_July26_weather_considerably_harder": "Made farming in the UK considerably harder",
      "McCain_July26_weather_somewhat_harder": "Made farming in the UK somewhat harder",
      "McCain_July26_weather_no_impact": "Had no impact on farming in the UK",
      "McCain_July26_weather_somewhat_easier": "Made farming in the UK somewhat easier",
      "McCain_July26_weather_considerably_easier": "Made farming in the UK considerably easier",
      "McCain_July26_weather_not_changed": "I don't think the weather has changed",
      "McCain_July26_weather_dk": "Don't know",
    },
    colors: {
      "McCain_July26_weather_considerably_harder": "#C62828",
      "McCain_July26_weather_somewhat_harder": "#EF5350",
      "McCain_July26_weather_no_impact": "#6B7280",
      "McCain_July26_weather_somewhat_easier": "#66BB6A",
      "McCain_July26_weather_considerably_easier": "#2E7D32",
      "McCain_July26_weather_not_changed": "#90A4AE",
      "McCain_July26_weather_dk": "#B0B8C4",
    },
    schema: "mccain",
    waves: [1],
  },

  // --- FS.8 A-K -----------------------------------------------------------
  // Policy order was randomised and split over two screens. Party
  // attribution for each policy is noted on the sub-variables below.
  farming_policies: {
    type: "likert_grid",
    label: "Farming policies: support or oppose",
    netLabel: "support for the policy",
    fulltext: "To what extent do you support or oppose each of the following suggested policies?",
    sidebarCompareSubtitle: "Compare net scores across all items, or drill into one.",
    sidebarCompareButton: "Compare all policies",
    sidebarDrillTitle: "Drill into a policy",
    sidebarDrillSubtitle: "Select an item for individual results.",
    sidebarDrillSubtitleTarget: "Select one item for targeting.",
    client: ["mccain", "stack-data-strategy"],
    category: "mccain",
    subVariables: [
      "policy_legally_binding",
      "policy_increase_funding",
      "policy_tying_funding",
      "policy_inheritance_tax",
      "policy_country_origin",
      "policy_farm_shops",
      "policy_regulator",
      "policy_public_sector",
      "policy_production_environment",
      "policy_paying_less_large",
      "policy_fast_track",
    ],
    subLabels: {
      "policy_legally_binding": "Legally binding production target",
      "policy_increase_funding": "Increase overall farming funding",
      "policy_tying_funding": "Tie funding to production",
      "policy_inheritance_tax": "Exempt farms from inheritance tax",
      "policy_country_origin": "Clearer country-of-origin labelling",
      "policy_farm_shops": "Exempt farm shops from business rates",
      "policy_regulator": "Regulator powers over supermarkets",
      "policy_public_sector": "Public sector buys British",
      "policy_production_environment": "Fund production and environment",
      "policy_paying_less_large": "Less to large farms, more to small",
      "policy_fast_track": "Fast-track farm planning",
    },
    subColors: {
      "policy_legally_binding": "#1565C0",
      "policy_increase_funding": "#2E7D32",
      "policy_tying_funding": "#F57C00",
      "policy_inheritance_tax": "#7B1FA2",
      "policy_country_origin": "#0097A7",
      "policy_farm_shops": "#C62828",
      "policy_regulator": "#558B2F",
      "policy_public_sector": "#AD1457",
      "policy_production_environment": "#4E342E",
      "policy_paying_less_large": "#00897B",
      "policy_fast_track": "#E64A19",
    },
    // Flat union of all sub-variable keys
    keys: [
      "McCain_July26_policy_legally_binding_strong_support",
      "McCain_July26_policy_legally_binding_somewhat_support",
      "McCain_July26_policy_legally_binding_neither",
      "McCain_July26_policy_legally_binding_somewhat_oppose",
      "McCain_July26_policy_legally_binding_strongly_oppose",
      "McCain_July26_policy_legally_binding_dk",
      "McCain_July26_policy_increase_funding_strong_support",
      "McCain_July26_policy_increase_funding_somewhat_support",
      "McCain_July26_policy_increase_funding_neither",
      "McCain_July26_policy_increase_funding_somewhat_oppose",
      "McCain_July26_policy_increase_funding_strongly_oppose",
      "McCain_July26_policy_increase_funding_dk",
      "McCain_July26_policy_tying_funding_strong_support",
      "McCain_July26_policy_tying_funding_somewhat_support",
      "McCain_July26_policy_tying_funding_neither",
      "McCain_July26_policy_tying_funding_somewhat_oppose",
      "McCain_July26_policy_tying_funding_strongly_oppose",
      "McCain_July26_policy_tying_funding_dk",
      "McCain_July26_policy_inheritance_tax_strong_support",
      "McCain_July26_policy_inheritance_tax_somewhat_support",
      "McCain_July26_policy_inheritance_tax_neither",
      "McCain_July26_policy_inheritance_tax_somewhat_oppose",
      "McCain_July26_policy_inheritance_tax_strongly_oppose",
      "McCain_July26_policy_inheritance_tax_dk",
      "McCain_July26_policy_country_origin_strong_support",
      "McCain_July26_policy_country_origin_somewhat_support",
      "McCain_July26_policy_country_origin_neither",
      "McCain_July26_policy_country_origin_somewhat_oppose",
      "McCain_July26_policy_country_origin_strongly_oppose",
      "McCain_July26_policy_country_origin_dk",
      "McCain_July26_policy_farm_shops_strong_support",
      "McCain_July26_policy_farm_shops_somewhat_support",
      "McCain_July26_policy_farm_shops_neither",
      "McCain_July26_policy_farm_shops_somewhat_oppose",
      "McCain_July26_policy_farm_shops_strongly_oppose",
      "McCain_July26_policy_farm_shops_dk",
      "McCain_July26_policy_regulator_strong_support",
      "McCain_July26_policy_regulator_somewhat_support",
      "McCain_July26_policy_regulator_neither",
      "McCain_July26_policy_regulator_somewhat_oppose",
      "McCain_July26_policy_regulator_strongly_oppose",
      "McCain_July26_policy_regulator_dk",
      "McCain_July26_policy_public_sector_strong_support",
      "McCain_July26_policy_public_sector_somewhat_support",
      "McCain_July26_policy_public_sector_neither",
      "McCain_July26_policy_public_sector_somewhat_oppose",
      "McCain_July26_policy_public_sector_strongly_oppose",
      "McCain_July26_policy_public_sector_dk",
      "McCain_July26_policy_production_environment_strong_support",
      "McCain_July26_policy_production_environment_somewhat_support",
      "McCain_July26_policy_production_environment_neither",
      "McCain_July26_policy_production_environment_somewhat_oppose",
      "McCain_July26_policy_production_environment_strongly_oppose",
      "McCain_July26_policy_production_environment_dk",
      "McCain_July26_policy_paying_less_large_strong_support",
      "McCain_July26_policy_paying_less_large_somewhat_support",
      "McCain_July26_policy_paying_less_large_neither",
      "McCain_July26_policy_paying_less_large_somewhat_oppose",
      "McCain_July26_policy_paying_less_large_strongly_oppose",
      "McCain_July26_policy_paying_less_large_dk",
      "McCain_July26_policy_fast_track_strong_support",
      "McCain_July26_policy_fast_track_somewhat_support",
      "McCain_July26_policy_fast_track_neither",
      "McCain_July26_policy_fast_track_somewhat_oppose",
      "McCain_July26_policy_fast_track_strongly_oppose",
      "McCain_July26_policy_fast_track_dk",
    ],
    labels: {},
    schema: "mccain",
    waves: [1],
  },

  // FS.8A, Reform policy
  policy_legally_binding: {
    type: "likert",
    hidden: true,
    label: "Policy: Legally binding production target",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? A legally binding target for any government to increase the proportion of food consumed here that's grown here",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_legally_binding_strong_support",
      "McCain_July26_policy_legally_binding_somewhat_support",
      "McCain_July26_policy_legally_binding_neither",
      "McCain_July26_policy_legally_binding_somewhat_oppose",
      "McCain_July26_policy_legally_binding_strongly_oppose",
      "McCain_July26_policy_legally_binding_dk",
    ],
    labels: {
      "McCain_July26_policy_legally_binding_strong_support": "Strongly support",
      "McCain_July26_policy_legally_binding_somewhat_support": "Somewhat support",
      "McCain_July26_policy_legally_binding_neither": "Neither support nor oppose",
      "McCain_July26_policy_legally_binding_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_legally_binding_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_legally_binding_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_legally_binding_strong_support": "#2E7D32",
      "McCain_July26_policy_legally_binding_somewhat_support": "#66BB6A",
      "McCain_July26_policy_legally_binding_neither": "#6B7280",
      "McCain_July26_policy_legally_binding_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_legally_binding_strongly_oppose": "#C62828",
      "McCain_July26_policy_legally_binding_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_legally_binding_strong_support", "McCain_July26_policy_legally_binding_somewhat_support"],
      neu: "McCain_July26_policy_legally_binding_neither",
      neg: ["McCain_July26_policy_legally_binding_somewhat_oppose", "McCain_July26_policy_legally_binding_strongly_oppose"],
      dk: "McCain_July26_policy_legally_binding_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8B, Reform policy
  policy_increase_funding: {
    type: "likert",
    hidden: true,
    label: "Policy: Increase overall farming funding",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Increasing the overall amount of government funding given to farms",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_increase_funding_strong_support",
      "McCain_July26_policy_increase_funding_somewhat_support",
      "McCain_July26_policy_increase_funding_neither",
      "McCain_July26_policy_increase_funding_somewhat_oppose",
      "McCain_July26_policy_increase_funding_strongly_oppose",
      "McCain_July26_policy_increase_funding_dk",
    ],
    labels: {
      "McCain_July26_policy_increase_funding_strong_support": "Strongly support",
      "McCain_July26_policy_increase_funding_somewhat_support": "Somewhat support",
      "McCain_July26_policy_increase_funding_neither": "Neither support nor oppose",
      "McCain_July26_policy_increase_funding_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_increase_funding_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_increase_funding_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_increase_funding_strong_support": "#2E7D32",
      "McCain_July26_policy_increase_funding_somewhat_support": "#66BB6A",
      "McCain_July26_policy_increase_funding_neither": "#6B7280",
      "McCain_July26_policy_increase_funding_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_increase_funding_strongly_oppose": "#C62828",
      "McCain_July26_policy_increase_funding_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_increase_funding_strong_support", "McCain_July26_policy_increase_funding_somewhat_support"],
      neu: "McCain_July26_policy_increase_funding_neither",
      neg: ["McCain_July26_policy_increase_funding_somewhat_oppose", "McCain_July26_policy_increase_funding_strongly_oppose"],
      dk: "McCain_July26_policy_increase_funding_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8C, Reform policy
  policy_tying_funding: {
    type: "likert",
    hidden: true,
    label: "Policy: Tie funding to production",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Tying government funding paid to farmers to how much food they produce, rather than how they look after the environment",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_tying_funding_strong_support",
      "McCain_July26_policy_tying_funding_somewhat_support",
      "McCain_July26_policy_tying_funding_neither",
      "McCain_July26_policy_tying_funding_somewhat_oppose",
      "McCain_July26_policy_tying_funding_strongly_oppose",
      "McCain_July26_policy_tying_funding_dk",
    ],
    labels: {
      "McCain_July26_policy_tying_funding_strong_support": "Strongly support",
      "McCain_July26_policy_tying_funding_somewhat_support": "Somewhat support",
      "McCain_July26_policy_tying_funding_neither": "Neither support nor oppose",
      "McCain_July26_policy_tying_funding_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_tying_funding_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_tying_funding_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_tying_funding_strong_support": "#2E7D32",
      "McCain_July26_policy_tying_funding_somewhat_support": "#66BB6A",
      "McCain_July26_policy_tying_funding_neither": "#6B7280",
      "McCain_July26_policy_tying_funding_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_tying_funding_strongly_oppose": "#C62828",
      "McCain_July26_policy_tying_funding_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_tying_funding_strong_support", "McCain_July26_policy_tying_funding_somewhat_support"],
      neu: "McCain_July26_policy_tying_funding_neither",
      neg: ["McCain_July26_policy_tying_funding_somewhat_oppose", "McCain_July26_policy_tying_funding_strongly_oppose"],
      dk: "McCain_July26_policy_tying_funding_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8D, Reform policy
  policy_inheritance_tax: {
    type: "likert",
    hidden: true,
    label: "Policy: Exempt farms from inheritance tax",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Exempting farming families from paying inheritance tax on their land",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_inheritance_tax_strong_support",
      "McCain_July26_policy_inheritance_tax_somewhat_support",
      "McCain_July26_policy_inheritance_tax_neither",
      "McCain_July26_policy_inheritance_tax_somewhat_oppose",
      "McCain_July26_policy_inheritance_tax_strongly_oppose",
      "McCain_July26_policy_inheritance_tax_dk",
    ],
    labels: {
      "McCain_July26_policy_inheritance_tax_strong_support": "Strongly support",
      "McCain_July26_policy_inheritance_tax_somewhat_support": "Somewhat support",
      "McCain_July26_policy_inheritance_tax_neither": "Neither support nor oppose",
      "McCain_July26_policy_inheritance_tax_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_inheritance_tax_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_inheritance_tax_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_inheritance_tax_strong_support": "#2E7D32",
      "McCain_July26_policy_inheritance_tax_somewhat_support": "#66BB6A",
      "McCain_July26_policy_inheritance_tax_neither": "#6B7280",
      "McCain_July26_policy_inheritance_tax_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_inheritance_tax_strongly_oppose": "#C62828",
      "McCain_July26_policy_inheritance_tax_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_inheritance_tax_strong_support", "McCain_July26_policy_inheritance_tax_somewhat_support"],
      neu: "McCain_July26_policy_inheritance_tax_neither",
      neg: ["McCain_July26_policy_inheritance_tax_somewhat_oppose", "McCain_July26_policy_inheritance_tax_strongly_oppose"],
      dk: "McCain_July26_policy_inheritance_tax_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8E, Reform policy
  policy_country_origin: {
    type: "likert",
    hidden: true,
    label: "Policy: Clearer country-of-origin labelling",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Requiring the country-of-origin to be clearer on food packaging",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_country_origin_strong_support",
      "McCain_July26_policy_country_origin_somewhat_support",
      "McCain_July26_policy_country_origin_neither",
      "McCain_July26_policy_country_origin_somewhat_oppose",
      "McCain_July26_policy_country_origin_strongly_oppose",
      "McCain_July26_policy_country_origin_dk",
    ],
    labels: {
      "McCain_July26_policy_country_origin_strong_support": "Strongly support",
      "McCain_July26_policy_country_origin_somewhat_support": "Somewhat support",
      "McCain_July26_policy_country_origin_neither": "Neither support nor oppose",
      "McCain_July26_policy_country_origin_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_country_origin_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_country_origin_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_country_origin_strong_support": "#2E7D32",
      "McCain_July26_policy_country_origin_somewhat_support": "#66BB6A",
      "McCain_July26_policy_country_origin_neither": "#6B7280",
      "McCain_July26_policy_country_origin_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_country_origin_strongly_oppose": "#C62828",
      "McCain_July26_policy_country_origin_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_country_origin_strong_support", "McCain_July26_policy_country_origin_somewhat_support"],
      neu: "McCain_July26_policy_country_origin_neither",
      neg: ["McCain_July26_policy_country_origin_somewhat_oppose", "McCain_July26_policy_country_origin_strongly_oppose"],
      dk: "McCain_July26_policy_country_origin_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8F, Reform policy
  policy_farm_shops: {
    type: "likert",
    hidden: true,
    label: "Policy: Exempt farm shops from business rates",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Exempting farm shops from paying business rates",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_farm_shops_strong_support",
      "McCain_July26_policy_farm_shops_somewhat_support",
      "McCain_July26_policy_farm_shops_neither",
      "McCain_July26_policy_farm_shops_somewhat_oppose",
      "McCain_July26_policy_farm_shops_strongly_oppose",
      "McCain_July26_policy_farm_shops_dk",
    ],
    labels: {
      "McCain_July26_policy_farm_shops_strong_support": "Strongly support",
      "McCain_July26_policy_farm_shops_somewhat_support": "Somewhat support",
      "McCain_July26_policy_farm_shops_neither": "Neither support nor oppose",
      "McCain_July26_policy_farm_shops_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_farm_shops_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_farm_shops_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_farm_shops_strong_support": "#2E7D32",
      "McCain_July26_policy_farm_shops_somewhat_support": "#66BB6A",
      "McCain_July26_policy_farm_shops_neither": "#6B7280",
      "McCain_July26_policy_farm_shops_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_farm_shops_strongly_oppose": "#C62828",
      "McCain_July26_policy_farm_shops_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_farm_shops_strong_support", "McCain_July26_policy_farm_shops_somewhat_support"],
      neu: "McCain_July26_policy_farm_shops_neither",
      neg: ["McCain_July26_policy_farm_shops_somewhat_oppose", "McCain_July26_policy_farm_shops_strongly_oppose"],
      dk: "McCain_July26_policy_farm_shops_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8G, Reform policy
  policy_regulator: {
    type: "likert",
    hidden: true,
    label: "Policy: Regulator powers over supermarkets",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Giving an official regulator more power to intervene if it thinks supermarkets are not paying farmers a fair price",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_regulator_strong_support",
      "McCain_July26_policy_regulator_somewhat_support",
      "McCain_July26_policy_regulator_neither",
      "McCain_July26_policy_regulator_somewhat_oppose",
      "McCain_July26_policy_regulator_strongly_oppose",
      "McCain_July26_policy_regulator_dk",
    ],
    labels: {
      "McCain_July26_policy_regulator_strong_support": "Strongly support",
      "McCain_July26_policy_regulator_somewhat_support": "Somewhat support",
      "McCain_July26_policy_regulator_neither": "Neither support nor oppose",
      "McCain_July26_policy_regulator_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_regulator_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_regulator_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_regulator_strong_support": "#2E7D32",
      "McCain_July26_policy_regulator_somewhat_support": "#66BB6A",
      "McCain_July26_policy_regulator_neither": "#6B7280",
      "McCain_July26_policy_regulator_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_regulator_strongly_oppose": "#C62828",
      "McCain_July26_policy_regulator_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_regulator_strong_support", "McCain_July26_policy_regulator_somewhat_support"],
      neu: "McCain_July26_policy_regulator_neither",
      neg: ["McCain_July26_policy_regulator_somewhat_oppose", "McCain_July26_policy_regulator_strongly_oppose"],
      dk: "McCain_July26_policy_regulator_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8H, Reform policy
  policy_public_sector: {
    type: "likert",
    hidden: true,
    label: "Policy: Public sector buys British",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Requiring the public sector (e.g. schools, hospitals) to buy food grown in the UK, or to similar standards",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_public_sector_strong_support",
      "McCain_July26_policy_public_sector_somewhat_support",
      "McCain_July26_policy_public_sector_neither",
      "McCain_July26_policy_public_sector_somewhat_oppose",
      "McCain_July26_policy_public_sector_strongly_oppose",
      "McCain_July26_policy_public_sector_dk",
    ],
    labels: {
      "McCain_July26_policy_public_sector_strong_support": "Strongly support",
      "McCain_July26_policy_public_sector_somewhat_support": "Somewhat support",
      "McCain_July26_policy_public_sector_neither": "Neither support nor oppose",
      "McCain_July26_policy_public_sector_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_public_sector_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_public_sector_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_public_sector_strong_support": "#2E7D32",
      "McCain_July26_policy_public_sector_somewhat_support": "#66BB6A",
      "McCain_July26_policy_public_sector_neither": "#6B7280",
      "McCain_July26_policy_public_sector_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_public_sector_strongly_oppose": "#C62828",
      "McCain_July26_policy_public_sector_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_public_sector_strong_support", "McCain_July26_policy_public_sector_somewhat_support"],
      neu: "McCain_July26_policy_public_sector_neither",
      neg: ["McCain_July26_policy_public_sector_somewhat_oppose", "McCain_July26_policy_public_sector_strongly_oppose"],
      dk: "McCain_July26_policy_public_sector_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8I, Labour / Conservative / Lib Dem / Green policy
  policy_production_environment: {
    type: "likert",
    hidden: true,
    label: "Policy: Fund production and environment",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Supporting farmers with government funds, tied to both how much food they produce and environmental protection",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_production_environment_strong_support",
      "McCain_July26_policy_production_environment_somewhat_support",
      "McCain_July26_policy_production_environment_neither",
      "McCain_July26_policy_production_environment_somewhat_oppose",
      "McCain_July26_policy_production_environment_strongly_oppose",
      "McCain_July26_policy_production_environment_dk",
    ],
    labels: {
      "McCain_July26_policy_production_environment_strong_support": "Strongly support",
      "McCain_July26_policy_production_environment_somewhat_support": "Somewhat support",
      "McCain_July26_policy_production_environment_neither": "Neither support nor oppose",
      "McCain_July26_policy_production_environment_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_production_environment_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_production_environment_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_production_environment_strong_support": "#2E7D32",
      "McCain_July26_policy_production_environment_somewhat_support": "#66BB6A",
      "McCain_July26_policy_production_environment_neither": "#6B7280",
      "McCain_July26_policy_production_environment_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_production_environment_strongly_oppose": "#C62828",
      "McCain_July26_policy_production_environment_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_production_environment_strong_support", "McCain_July26_policy_production_environment_somewhat_support"],
      neu: "McCain_July26_policy_production_environment_neither",
      neg: ["McCain_July26_policy_production_environment_somewhat_oppose", "McCain_July26_policy_production_environment_strongly_oppose"],
      dk: "McCain_July26_policy_production_environment_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8J, Green policy
  policy_paying_less_large: {
    type: "likert",
    hidden: true,
    label: "Policy: Less to large farms, more to small",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Paying less government money to large-scale farms, and more to smaller ones that follow organic and ecological methods",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_paying_less_large_strong_support",
      "McCain_July26_policy_paying_less_large_somewhat_support",
      "McCain_July26_policy_paying_less_large_neither",
      "McCain_July26_policy_paying_less_large_somewhat_oppose",
      "McCain_July26_policy_paying_less_large_strongly_oppose",
      "McCain_July26_policy_paying_less_large_dk",
    ],
    labels: {
      "McCain_July26_policy_paying_less_large_strong_support": "Strongly support",
      "McCain_July26_policy_paying_less_large_somewhat_support": "Somewhat support",
      "McCain_July26_policy_paying_less_large_neither": "Neither support nor oppose",
      "McCain_July26_policy_paying_less_large_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_paying_less_large_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_paying_less_large_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_paying_less_large_strong_support": "#2E7D32",
      "McCain_July26_policy_paying_less_large_somewhat_support": "#66BB6A",
      "McCain_July26_policy_paying_less_large_neither": "#6B7280",
      "McCain_July26_policy_paying_less_large_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_paying_less_large_strongly_oppose": "#C62828",
      "McCain_July26_policy_paying_less_large_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_paying_less_large_strong_support", "McCain_July26_policy_paying_less_large_somewhat_support"],
      neu: "McCain_July26_policy_paying_less_large_neither",
      neg: ["McCain_July26_policy_paying_less_large_somewhat_oppose", "McCain_July26_policy_paying_less_large_strongly_oppose"],
      dk: "McCain_July26_policy_paying_less_large_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // FS.8K, Lib Dem policy
  policy_fast_track: {
    type: "likert",
    hidden: true,
    label: "Policy: Fast-track farm planning",
    category: "mccain",
    fulltext: "To what extent do you support or oppose each of the following suggested policies? Fast-tracking the planning process for farming infrastructure (e.g. grain stores, reservoirs)",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_policy_fast_track_strong_support",
      "McCain_July26_policy_fast_track_somewhat_support",
      "McCain_July26_policy_fast_track_neither",
      "McCain_July26_policy_fast_track_somewhat_oppose",
      "McCain_July26_policy_fast_track_strongly_oppose",
      "McCain_July26_policy_fast_track_dk",
    ],
    labels: {
      "McCain_July26_policy_fast_track_strong_support": "Strongly support",
      "McCain_July26_policy_fast_track_somewhat_support": "Somewhat support",
      "McCain_July26_policy_fast_track_neither": "Neither support nor oppose",
      "McCain_July26_policy_fast_track_somewhat_oppose": "Somewhat oppose",
      "McCain_July26_policy_fast_track_strongly_oppose": "Strongly oppose",
      "McCain_July26_policy_fast_track_dk": "Don't know",
    },
    colors: {
      "McCain_July26_policy_fast_track_strong_support": "#2E7D32",
      "McCain_July26_policy_fast_track_somewhat_support": "#66BB6A",
      "McCain_July26_policy_fast_track_neither": "#6B7280",
      "McCain_July26_policy_fast_track_somewhat_oppose": "#EF5350",
      "McCain_July26_policy_fast_track_strongly_oppose": "#C62828",
      "McCain_July26_policy_fast_track_dk": "#B0B8C4",
    },
    polarity: {
      pos: ["McCain_July26_policy_fast_track_strong_support", "McCain_July26_policy_fast_track_somewhat_support"],
      neu: "McCain_July26_policy_fast_track_neither",
      neg: ["McCain_July26_policy_fast_track_somewhat_oppose", "McCain_July26_policy_fast_track_strongly_oppose"],
      dk: "McCain_July26_policy_fast_track_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // --- FS.10 --------------------------------------------------------------
  // Single-select.
  support_farmers_approach: {
    type: "categorical",
    label: "Best approach to supporting farmers with public funds",
    category: "mccain",
    fulltext: "From the following options, which one do you feel is the best approach to supporting UK farmers with public funds? Financial support to farmers from public funds should...",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_support_approach_how_much_food",
      "McCain_July26_support_approach_look_after_environment",
      "McCain_July26_support_approach_both",
      "McCain_July26_support_approach_not_paid",
      "McCain_July26_support_approach_dk",
    ],
    labels: {
      "McCain_July26_support_approach_how_much_food": "...be mainly based on how much food they produce",
      "McCain_July26_support_approach_look_after_environment": "...be mainly based on how well they look after the environment",
      "McCain_July26_support_approach_both": "...be based on both how much food they produce and how well they look after the environment",
      "McCain_July26_support_approach_not_paid": "...not be paid at all",
      "McCain_July26_support_approach_dk": "Don't know",
    },
    colors: {
      "McCain_July26_support_approach_how_much_food": "#1565C0",
      "McCain_July26_support_approach_look_after_environment": "#2E7D32",
      "McCain_July26_support_approach_both": "#7B1FA2",
      "McCain_July26_support_approach_not_paid": "#C62828",
      "McCain_July26_support_approach_dk": "#B0B8C4",
    },
    schema: "mccain",
    waves: [1],
  },

   // --- BFG.1 --------------------------------------------------------------
  // Asked of everyone. It is BFG.2 below that is filtered, on the answer given
  // here.
  //
  // THE MODELLED COLUMNS DO NOT FORM A DISTRIBUTION. Since every respondent
  // answers, the five points should sum to 100 per area. They sum to between
  // 180% and 377% depending on the seat:
  //
  //   mostly_unfair + almost_always_unfair == 100.00 in all 632 seats
  //   mostly_fair alone reaches 178%
  //   almost_always_fair + mostly_fair spans 63% to 235%
  //
  // Two points of a five-point scale cannot account for 100% of everyone, so
  // the unfair pair has been normalised against something other than the full
  // base. Consequences: any "% who say payment is unfair" built by summing the
  // two unfair points is 100 by construction, and no share of the fair side is
  // recoverable either. The McCain report shows the resulting 100% knowingly —
  // see the panel comment in McCainReportView.js. Fix belongs in the
  // stratification.
  farmers_paid_fair: {
    type: "likert",
    label: "Is the way farmers get paid fair",
    category: "mccain",
    fulltext: "Now thinking about how farmers get paid - for example, by supermarkets and food manufacturers - what is your sense as to how fair or not this is to farmers?  Even if you are not sure, we are interested in your general perceptions and feelings. ",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_farmers_paid_almost_always_fair",
      "McCain_July26_farmers_paid_mostly_fair",
      "McCain_July26_farmers_paid_mostly_unfair",
      "McCain_July26_farmers_paid_almost_always_unfair",
      "McCain_July26_farmers_paid_dk",
    ],
    labels: {
      "McCain_July26_farmers_paid_almost_always_fair":"Almost always fair",
      "McCain_July26_farmers_paid_mostly_fair":"Mostly fair",
      "McCain_July26_farmers_paid_mostly_unfair":"Mostly unfair",
      "McCain_July26_farmers_paid_almost_always_unfair":"Almost always unfair",
      "McCain_July26_farmers_paid_dk":"Don't know",
    },
    polarity: {
      pos: ["McCain_July26_farmers_paid_almost_always_fair","McCain_July26_farmers_paid_mostly_fair"],
      neg: ["McCain_July26_farmers_paid_mostly_unfair","McCain_July26_farmers_paid_almost_always_unfair"],
      dk: "McCain_July26_farmers_paid_dk",
    },
    schema: "mccain",
    waves: [1],
  },

  // --- BFG.2 --------------------------------------------------------------
  // FILTERED QUESTION — base is respondents who said payment is mostly or
  // almost always unfair at BFG.1. BFG.1 was not modelled, so the filter
  // base is not available in this schema.
  // ALSO MULTI-SELECT, up to two picks: values are shares of selections.
  // Options 3-5 are McCain commitments; option 1 is a Reform proposal and
  // option 2 is an NFU proposal.
  effective_farmers_fair: {
    type: "categorical",
    label: "Most effective route to fair payment for farmers",
    category: "mccain",
    fulltext: "Which of the following do you think would be most effective in ensuring farmers get paid fairly for what they produce?",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_effective_fair_official_regulator",
      "McCain_July26_effective_fair_legally_enforceable",
      "McCain_July26_effective_fair_pay_more",
      "McCain_July26_effective_fair_pay_before_harvest",
      "McCain_July26_effective_fair_foreign_producers",
      "McCain_July26_effective_fair_dk",
    ],
    labels: {
      "McCain_July26_effective_fair_official_regulator": "Giving an official regulator more power to intervene if it thinks food companies are not paying farmers a fair price (e.g. by imposing fines)",
      "McCain_July26_effective_fair_legally_enforceable": "Creating legally enforceable rules on how large buyers should deal with farmers (e.g. agreeing in advance on conditions for future price changes)",
      "McCain_July26_effective_fair_pay_more": "Buyers being prepared to pay more when prices rise",
      "McCain_July26_effective_fair_pay_before_harvest": "Buyers paying some cash before harvest, to help farmers with cash flow",
      "McCain_July26_effective_fair_foreign_producers": "Ensuring foreign producers cannot undercut UK farmers by following lower standards",
      "McCain_July26_effective_fair_dk": "Don't know",
    },
    colors: {
      "McCain_July26_effective_fair_official_regulator": "#1565C0",
      "McCain_July26_effective_fair_legally_enforceable": "#2E7D32",
      "McCain_July26_effective_fair_pay_more": "#F57C00",
      "McCain_July26_effective_fair_pay_before_harvest": "#7B1FA2",
      "McCain_July26_effective_fair_foreign_producers": "#0097A7",
      "McCain_July26_effective_fair_dk": "#B0B8C4",
    },
    schema: "mccain",
    weightColumn: "N_BFG1_UNFAIR",
    waves: [1],
  },

    efficiency_v_sustainability: {
    type: "categorical",
    label: "Farming now vs farming for the next generation",
    category: "mccain",
    fulltext: "Some people say that we should prioritise making farming as efficient and financially sustainable for farmers now. Other people say we should prioritise preserving farming resources such as land, soil quality, and water for the next generation of farmers. What is your own view on this? ",
    client: ["mccain", "stack-data-strategy"],
    keys: [
      "McCain_July26_FS4_efficiency_v_sustainability_maximise_efficiency_now",
      "McCain_July26_FS4_efficiency_v_sustainability_neither",
      "McCain_July26_FS4_efficiency_v_sustainability_preserve_for_future",
    ],
    labels: {
      "McCain_July26_FS4_efficiency_v_sustainability_maximise_efficiency_now": "Prioritise making farming efficient and financially sustainable now (0-3 on the slider)",
      "McCain_July26_FS4_efficiency_v_sustainability_neither": "Neither / middle of the scale (4-6)",
      "McCain_July26_FS4_efficiency_v_sustainability_preserve_for_future": "Prioritise preserving land, soil quality and water for the next generation of farmers (7-10)",
    },
    colors: {
      "McCain_July26_FS4_efficiency_v_sustainability_maximise_efficiency_now": "#FF6D00",
      "McCain_July26_FS4_efficiency_v_sustainability_neither": "#6B7280",
      "McCain_July26_FS4_efficiency_v_sustainability_preserve_for_future": "#1739e5",
    },
    polarity: {
      pos: ["McCain_July26_FS4_efficiency_v_sustainability_preserve_for_future"],
      neu: "McCain_July26_FS4_efficiency_v_sustainability_neither",
      neg: ["McCain_July26_FS4_efficiency_v_sustainability_maximise_efficiency_now"],
    },
    schema: "mccain",
    waves: [1],
  },

};